import { useEffect, useRef, useState } from "react";

interface VideoPlayerProps {
  poster: string;
  title: string;
}

type PlayerStatus = "idle" | "loading" | "playing" | "paused";

const TOTAL_SECONDS = 120;

function formatTime(sec: number) {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function VideoPlayer({ poster, title }: VideoPlayerProps) {
  const [status, setStatus] = useState<PlayerStatus>("idle");
  const [progress, setProgress] = useState(0);
  const timerRef = useRef<number | null>(null);
  const loadingRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
      if (loadingRef.current) window.clearTimeout(loadingRef.current);
    };
  }, []);

  const startPlaying = () => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = window.setInterval(() => {
      setProgress((p) => {
        if (p >= TOTAL_SECONDS) {
          if (timerRef.current) window.clearInterval(timerRef.current);
          setStatus("idle");
          return 0;
        }
        return p + 0.5;
      });
    }, 250);
  };

  const handlePlay = () => {
    if (status === "playing") return;
    if (status === "paused") {
      setStatus("playing");
      startPlaying();
      return;
    }
    setStatus("loading");
    loadingRef.current = window.setTimeout(() => {
      setStatus("playing");
      setProgress(0);
      startPlaying();
    }, 1200);
  };

  const handlePause = () => {
    if (status !== "playing") return;
    setStatus("paused");
    if (timerRef.current) window.clearInterval(timerRef.current);
  };

  const handleRestart = () => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    setProgress(0);
    setStatus("idle");
  };

  const isPlaying = status === "playing";
  const isActive = status === "playing" || status === "paused";
  const percent = (progress / TOTAL_SECONDS) * 100;

  return (
    <div className="w-full">
      <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-foreground-950">
        <img
          src={poster}
          alt={`${title} 播放画面`}
          title={`${title} - 在线视频`}
          className={`w-full h-full object-cover object-top transition-all duration-500 ${
            isActive ? "opacity-60" : "opacity-100"
          }`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/20 to-foreground-950/30"></div>

        {/* Demo badge */}
        <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-accent-500 text-background-50 dark:text-foreground-950 text-xs font-semibold">
          <i className="ri-information-line"></i>
          前端模拟播放
        </span>

        {/* Center control */}
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-center px-6">
          {status === "loading" && (
            <>
              <span className="w-14 h-14 flex items-center justify-center">
                <i className="ri-loader-4-line text-4xl text-white animate-spin"></i>
              </span>
              <p className="text-sm text-background-200">正在缓冲，马上开始…</p>
            </>
          )}

          {(status === "idle" || status === "paused") && (
            <>
              <button
                type="button"
                onClick={handlePlay}
                aria-label="播放"
                className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500 text-background-50 dark:text-foreground-950 hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-play-fill text-4xl md:text-5xl pl-0.5"></i>
              </button>
              <p className="max-w-md text-sm text-background-200 line-clamp-2">
                《{title}》 · 当前为演示播放器，暂无真实视频源
              </p>
              {status === "paused" && (
                <p className="text-xs text-background-300">
                  已暂停 · {formatTime(progress)} / {formatTime(TOTAL_SECONDS)}
                </p>
              )}
            </>
          )}

          {isPlaying && (
            <p className="text-xs text-background-300">
              {formatTime(progress)} / {formatTime(TOTAL_SECONDS)}
            </p>
          )}
        </div>

        {/* Progress bar */}
        <div className="absolute bottom-0 left-0 right-0 px-3 pb-3">
          <div className="h-1 w-full rounded-full bg-background-50/25 overflow-hidden">
            <div
              className="h-full bg-primary-500 transition-[width] duration-200"
              style={{ width: `${percent}%` }}
            ></div>
          </div>
          <div className="mt-2 flex items-center justify-between">
            <div className="flex items-center gap-3 text-white">
              <button
                type="button"
                onClick={isPlaying ? handlePause : handlePlay}
                aria-label={isPlaying ? "暂停" : "播放"}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-50/20 cursor-pointer"
              >
                <i className={isPlaying ? "ri-pause-fill text-xl" : "ri-play-fill text-xl"}></i>
              </button>
              <button
                type="button"
                onClick={handleRestart}
                aria-label="重新播放"
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-50/20 cursor-pointer"
              >
                <i className="ri-restart-line text-lg"></i>
              </button>
            </div>
            <span className="text-xs text-background-200">
              演示模式 · 无真实播放
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}